import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import type { Trip } from "./schemas";

export function useTrips() {
  return useQuery({
    queryKey: ["trips"],
    queryFn: () => apiClient.get<Trip[]>("/v1/trips"),
  });
}

export function useTrip(id: string | undefined) {
  return useQuery({
    queryKey: ["trips", id],
    queryFn: () => apiClient.get<Trip>(`/v1/trips/${id}`),
    enabled: !!id,
  });
}

export function useCreateTrip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { title?: string; destination?: string; query?: string }) =>
      apiClient.post<Trip>("/v1/trips", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    },
  });
}

export function useUpdateTrip(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { title?: string; destination?: string }) =>
      apiClient.put<Trip>(`/v1/trips/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["trips", id] });
    },
  });
}

export function useUpdateItinerary(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itinerary: Trip["itinerary"]) =>
      apiClient.put<Trip>(`/v1/trips/${id}/itinerary`, { itinerary }),
    onSuccess: (trip) => {
      queryClient.setQueryData(["trips", id], trip);
    },
  });
}

export function useDeleteTrip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/v1/trips/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    },
  });
}

export function useShareTrip(id: string) {
  return useMutation({
    mutationFn: () => apiClient.post<{ shareToken: string }>(`/v1/trips/${id}/share`),
  });
}

export interface SaveSharedInput {
  title?: string;
  startDate?: string;
  endDate?: string;
  travelersCount?: number;
  includeChat?: boolean;
}

// Clones a shared trip into the authenticated user's own trips.
// Backend returns 409 with { tripId } when the requester already
// owns the source — `onConflict` surfaces that so the caller can
// redirect instead of creating a duplicate.
export function useSaveSharedTrip(
  token: string | undefined,
  options?: { onConflict?: (existingTripId: string) => void }
) {
  const queryClient = useQueryClient();
  return useMutation<Trip, Error, SaveSharedInput>({
    mutationFn: async (input: SaveSharedInput) => {
      if (!token) throw new Error("missing share token");
      try {
        return await apiClient.post<Trip>(
          `/v1/shared/${token}/save`,
          input ?? {}
        );
      } catch (err: any) {
        if (err?.status === 409 && err?.body?.tripId && options?.onConflict) {
          options.onConflict(err.body.tripId);
        }
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    },
  });
}

export function useSharedTrip(token: string | undefined) {
  return useQuery({
    queryKey: ["shared", token],
    queryFn: () => apiClient.get<Trip>(`/v1/shared/${token}`),
    enabled: !!token,
  });
}

export interface ConversationMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

export function useChatHistory(tripId: string | undefined) {
  return useQuery({
    queryKey: ["chat-history", tripId],
    queryFn: () =>
      apiClient.get<ConversationMessage[]>(`/v1/trips/${tripId}/chat`),
    enabled: !!tripId,
  });
}
